import React, { useState, useRef, useEffect } from 'react';
import { render } from 'react-dom';
import { useInView } from "react-intersection-observer";
import ReactPlayerLoader from "../shared/components/BrightcoveReactPlayer";
import VideoPlaylist from './components/VideoPlaylist';
import PlayDirectionIcon from "../shared/svg/PlayDirectionIcon";
import useInterval from "../shared/hooks/useInterval";
import useResize from "../shared/hooks/useResize";
import ReplayIcon from "../shared/svg/ReplayIcon/ReplayIcon";
import { doubleDigitIntDisplay } from "../../libraries/_helpers";
import { actionTypes, useVideo } from "../shared/hooks/useVideo";
import { useReactAppState } from "../shared/containers/utils";
import { getUserPlayVideoPermission, isMobileBrowser } from './utils';
import useAuth0Session from './hooks/useAuth0Session';
import RequiredAuthModal from './components/RequiredAuthModal';

export const BrightCoveVideoPlaylist = ({ options }) => {
  if (!options.videoList) {
    return null;
  }

  const { mediaQueries, urlList, t } = useReactAppState();
  const { defaultAdUrl } = urlList || {};
  const { isMediumLargeMin } = mediaQueries || {};
  const isUserLogged = useAuth0Session();
  const [modalVisible, setModalVisible] = useState(false);

  const { state, updateVideo } = useVideo({
    initialState: {
      disableUrlFragment: options.disableUrlFragment || false,
      isVideoPlaying: false,
      isTimerRunning: false,
      isPlayerLoaded: false,
      count: null,
      isAccountIdChanged: false,
      videoList: options.videoList,
      initialVideoPosition: options.initialVideoPosition,
      isAutoplay: options.isAutoplay
    }
  });

  const { currentVideoPos, currentVideo, videoList, isVideoPlaying, isTimerRunning, isPlayerLoaded, count, isAutoplay } = state || {};
  const { accountWall, brightcoveAccountId, brightcoveId } = currentVideo?.fields || {};


  if (!videoList || !currentVideo) {
    return null;
  }

  const [playerHeight, setPlayerHeight] = useState(null);
  const _child = useRef(null);
  const mediaPlayerRef = useRef(null);
  const mediaPlayerBodyRef = useRef(null);

  const { ref, inView } = useInView({
    threshold: .5,
  });

  useEffect(() => {
    if (_child?.current?.player) {
      if (!inView) {
        _child.current.player.pause();
      }
    }
  }, [ref, inView, _child]);

  const goNextVideo = () => {
    const nextVideoPos = currentVideoPos + 1;
    if (nextVideoPos >= 0 && nextVideoPos < videoList.length) {
      updateVideo({ type: actionTypes.currentVideoPos, payload: nextVideoPos });
    }
    else {
      updateVideo({ type: actionTypes.currentVideoPos, payload: 0 });
    }
  };

  const goPrevVideo = () => {
    const prevVideoPos = currentVideoPos - 1;
    if (prevVideoPos >= 0) {
      updateVideo({ type: actionTypes.currentVideoPos, payload: prevVideoPos });
    }
  };

  function handleResize() {
    if (mediaPlayerRef?.current?.offsetWidth && mediaPlayerBodyRef?.current?.offsetHeight) {
      const playerHeight = (mediaPlayerRef.current.offsetWidth / 1.77) + mediaPlayerBodyRef.current.offsetHeight;
      setPlayerHeight(playerHeight);
    }
  }

  useEffect(() => {
    handleResize();
  }, [isPlayerLoaded, currentVideoPos]);

  useResize(handleResize, 1000);

  useInterval(() => {
    updateVideo({ type: actionTypes.count, payload: count - 1 });
  }, isTimerRunning ? 1000 : null);

  useEffect(() => {
    const endScreen = _child?.current?.refNode.querySelector('.mls-o-video-player__end-screen');
    if (isTimerRunning && endScreen) {
      endScreen.className = `mls-o-video-player__end-screen`;

      render(
        <div className="mls-o-video-player__end-screen-details">
          <div className="mls-o-video-player__end-screen-counter-wrapper">Next Video in: <span className="mls-o-video-player__end-screen-counter">{doubleDigitIntDisplay(count)}</span></div>
          <div className="mls-o-video-player__end-screen-button-group">
            <div className="mls-o-video-player__end-screen-replay">
              <ReplayIcon handleClick={() => _child.current.player.play()} size="large" />
              <div className="mls-o-video-player__end-screen-text">{t("replay_video")}</div>
            </div>
            <div className="mls-o-video-player__end-screen-next">
              <PlayDirectionIcon handleClick={() => goNextVideo()} size="large" />
              <div className="mls-o-video-player__end-screen-text">{t("next_video")}</div>
            </div>
          </div>
        </div>,
        endScreen
      );
    }

    if (count === 0 && isTimerRunning) {
      endScreen.className = `mls-o-video-player__end-screen vjs-hidden`;
      updateVideo({ type: actionTypes.isTimerRunning, payload: false });
      goNextVideo();
    }
  }, [count, isTimerRunning]);

  useEffect(() => {
    if (isVideoPlaying && _child?.current?.refNode.querySelector('.mls-o-video-player__end-screen')) {
      const endScreen = _child?.current?.refNode.querySelector('.mls-o-video-player__end-screen');
      endScreen.className = `mls-o-video-player__end-screen vjs-hidden`;
    }
  }, [isVideoPlaying]);

  const addIcons = (brightCovePlayerInstance) => {
    const $player = brightCovePlayerInstance?.el();

    if ($player) {
      const logo = document.createElement('div');
      logo.className = `vjs-control mls-o-video-player__logo`;

      const nextBtn = document.createElement('div');
      nextBtn.className = `vjs-control mls-o-video-player__next-button`;

      const prevBtn = document.createElement('div');
      prevBtn.className = `vjs-control mls-o-video-player__prev-button`;

      const progressContainer = document.createElement('div');
      progressContainer.className = 'mls-o-video-player__progress-container';

      const endScreen = document.createElement('div');
      endScreen.className = `mls-o-video-player__end-screen vjs-hidden`;

      const $duration = $player.querySelector('.vjs-duration.vjs-time-control.vjs-control');
      const $currentTime = $player.querySelector('.vjs-current-time.vjs-time-control.vjs-control');
      const $progressBar = $player.querySelector('.vjs-progress-control.vjs-control');

      progressContainer.append($currentTime);
      progressContainer.append($progressBar);
      progressContainer.append($duration);

      const controlBar = $player.querySelector('.vjs-control-bar');
      // remove time divider
      controlBar.removeChild($player.querySelector('.vjs-time-divider'));

      // Change the class name here to move the icon in the controlBar
      const volumeControlNode = $player.querySelector('.vjs-control-bar .vjs-volume-panel');
      // Insert the icon div in proper location
      controlBar.prepend(progressContainer);
      controlBar.insertBefore(nextBtn, volumeControlNode);
      controlBar.insertBefore(prevBtn, nextBtn);
      controlBar.append(logo);

      const liveLabel = document.createElement('div');
      liveLabel.className = 'mls-o-video-player__is-live';
      controlBar.insertBefore(liveLabel, volumeControlNode.nextSibling);

      const progressBall = document.createElement('div');
      progressBall.className = 'mls-o-video-player__progress-ball';
      const secondProgressBall = progressBall.cloneNode();

      const $volumeLevel = $player.querySelector('.vjs-volume-level');
      const $playProgress = $player.querySelector('.vjs-play-progress');
      $volumeLevel.append(progressBall);
      $playProgress.append(secondProgressBall);
      $player.append(endScreen);

      // setIsPlayerLoaded(true);
      updateVideo({ type: actionTypes.isPlayerLoaded, payload: true });
    }
  };

  useEffect(() => {
    const prevButton = _child?.current?.refNode.querySelector('.mls-o-video-player__prev-button');
    const nextButton = _child?.current?.refNode.querySelector('.mls-o-video-player__next-button');
    const liveLabel = _child?.current?.refNode.querySelector('.mls-o-video-player__is-live');

    if (liveLabel) {
      if (currentVideo?.context?.neutralSlug)
        render(<div className="mls-o-label">{currentVideo?.context?.neutralSlug}</div>, liveLabel);
      else
        render(null, liveLabel);
    }

    if (prevButton) {
      prevButton.className = `vjs-control mls-o-video-player__prev-button ${currentVideoPos > 0 ? '' : 'd-none'}`;

      render(
        <PlayDirectionIcon extraClasses="rotate--180" handleClick={goPrevVideo} />,
        prevButton
      );
    }

    if (nextButton) {
      nextButton.className = `vjs-control mls-o-video-player__next-button ${currentVideoPos < videoList.length - 1 ? '' : 'd-none'}`;

      render(
        <PlayDirectionIcon handleClick={goNextVideo} />,
        nextButton
      );
    }

    return () => {
      updateVideo({ type: actionTypes.isPlayerLoaded, payload: false });
      updateVideo({ type: actionTypes.isTimerRunning, payload: false });
    };
  }, [isPlayerLoaded, currentVideoPos]);

  const canUserPlayVideo = getUserPlayVideoPermission({ accountWall, isUserLogged });
  const autoPlayVideoPlayer = canUserPlayVideo ? isAutoplay : false;

  const isMobile = isMobileBrowser();

  console.log(isUserLogged)
  return (
    <div className="mls-c-video-component">
      {
        videoList.length > 0 && typeof isUserLogged == "boolean"
          ? (
            <>
              <RequiredAuthModal accountWall={accountWall} visible={modalVisible} onGoBackClick={() => setModalVisible(false)} />
              <div key={brightcoveAccountId} ref={mediaPlayerRef} className="mls-c-video-component__player d3-o-media-object">
                <div ref={ref} className="d3-o-media-object__figure">
                  <ReactPlayerLoader
                    ref={_child}
                    accountId={brightcoveAccountId}
                    videoId={brightcoveId}
                    accountWall={accountWall}
                    key={brightcoveId}
                    onSuccess={(success) => {
                      const videoPlayerRef = success?.ref;

                      addIcons(success?.ref);
                      videoPlayerRef.ima3.settings.serverUrl = currentVideo?.fields?.adUrl || defaultAdUrl || "";

                      const canUserPlayVideo = getUserPlayVideoPermission({ accountWall, isUserLogged });
                      if (!canUserPlayVideo) {
                        videoPlayerRef.off();
                        videoPlayerRef.controlBar?.progressControl?.disable();
                        videoPlayerRef.controlBar?.fullscreenToggle?.disable();
                        updateVideo({ type: actionTypes.isVideoPlaying, payload: false });
                        
                        if (isAutoplay) {
                          setModalVisible(true);
                        }

                        function handleVideoPlay() {
                          const canUserPlayVideo = getUserPlayVideoPermission({ accountWall, isUserLogged });

                          if (!canUserPlayVideo) {
                            setModalVisible(true);
                            videoPlayerRef.pause();
                            updateVideo({ type: actionTypes.isVideoPlaying, payload: false });
                          }
                        }
                        // videoPlayerRef.on('click', handleVideoPlay)
                        videoPlayerRef.on('touchend', handleVideoPlay)
                        videoPlayerRef.on('play', handleVideoPlay)

                      } else {
                        if(isVideoPlaying || (isMobile && state.isAutoplay)) {
                          videoPlayerRef.play();
                        }

                        videoPlayerRef.on("userinactive", () => {
                          const controlBar = videoPlayerRef.el().querySelector(".vjs-control-bar");
                          controlBar.style.opacity = 0;
                          controlBar.style.visibility = "visible";
                          controlBar.style.pointerEvents = "none";
                          controlBar.style.transition = "visibility 1s,opacity 1s";
                        });

                        videoPlayerRef.on("useractive", () => {
                          const controlBar = videoPlayerRef.el().querySelector(".vjs-control-bar");
                          controlBar.style = null;
                        });

                        videoPlayerRef.on('play', () => {
                          const canUserPlayVideo = getUserPlayVideoPermission({ accountWall, isUserLogged });

                          if (canUserPlayVideo) {
                            updateVideo({ type: actionTypes.isVideoPlaying, payload: true });
                            updateVideo({ type: actionTypes.isTimerRunning, payload: false });
                            updateVideo({ type: actionTypes.count, payload: options.timeBetweenVideos || 5 });

                            if (videoPlayerRef.el().classList.contains('vjs-controls-disabled')) {
                              videoPlayerRef.el().classList.remove('vjs-controls-disabled');
                            }
                          } else {
                            videoPlayerRef.pause();
                            updateVideo({ type: actionTypes.isVideoPlaying, payload: false });
                          }
                        });

                        videoPlayerRef.on('pause', () => {
                          updateVideo({ type: actionTypes.isVideoPlaying, payload: false });
                        });

                        videoPlayerRef.on('ended', () => {
                          updateVideo({ type: actionTypes.isVideoPlaying, payload: false });
                          updateVideo({ type: actionTypes.isTimerRunning, payload: true });
                          if (!videoPlayerRef.el().classList.contains('vjs-controls-disabled')) {
                            videoPlayerRef.el().classList.add('vjs-controls-disabled');
                          }
                        });
                      }
                    }}
                    attrs={{ className: 'mls-o-video-player' }}
                    embedType="in-page"
                    options={{ fluid: true, muted: true, preload: 'auto', playsinline: true, autoplay: isMobile ? false : autoPlayVideoPlayer }}
                  />
                </div>
                <div ref={mediaPlayerBodyRef} className="d3-o-media-object__body">
                  <div className="d3-o-media-object__title">
                    {videoList[currentVideoPos]?.title}
                  </div>
                  <div className="d3-o-media-object__description">
                    {videoList[currentVideoPos]?.fields?.description}
                  </div>
                </div>
              </div>
              <VideoPlaylist
                isVideoPlaying={isVideoPlaying}
                showLoadMore={videoList.length >= 6 && !isMediumLargeMin}
                playerHeight={playerHeight && isMediumLargeMin ? playerHeight : null}
                videoList={videoList}
                currentVideoID={currentVideo?.fields?.brightcoveId}
                updateVideo={updateVideo} />
            </>
          )
          : null
      }
    </div>
  );
};

export default BrightCoveVideoPlaylist;
